import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { afterStepEnds, clampStep, type TutorialLesson, type TutorialStep } from './tutorialsModel';

/**
 * Playback of a lesson's steps: one `<audio>` element plays each step's narration and the next step starts when it
 * ends; a step without audio (or whose audio fails) stays on screen for its `durationMs`. The audio source is set
 * imperatively when a step starts, so a manifest refetch (new signed URLs) never interrupts what is playing; a
 * resume after the refetch picks up the new URL at the same second.
 */

export interface LessonPlayer {
  audioRef: RefObject<HTMLAudioElement | null>;
  index: number;
  step: TutorialStep;
  playing: boolean;
  /** 0..1 within the current step. */
  progress: number;
  finished: boolean;
  /** The browser refused to start the audio without a gesture (NotAllowedError): the stage asks for one tap. */
  blocked: boolean;
  goTo: (index: number) => void;
  toggle: () => void;
  pause: () => void;
  /** Handlers for the `<audio>` element. */
  audioEvents: { onTimeUpdate: () => void; onEnded: () => void; onError: () => void };
}

interface Options {
  initialIndex: number;
  autoPlay?: boolean;
  onStep: (index: number) => void;
  onFinished: () => void;
}

/** Loads the step's audio unless it is already loaded (a new signed URL keeps the position), then plays it. */
function playAudio(audio: HTMLAudioElement, step: TutorialStep, loaded: { key: string; url: string } | null, onBlocked: () => void) {
  const url = step.audio ?? '';
  if (!loaded || loaded.key !== step.key) audio.src = url;
  else if (loaded.url !== url) {
    const at = audio.currentTime;
    audio.src = url;
    audio.addEventListener('loadedmetadata', () => (audio.currentTime = at), { once: true });
  }
  audio.play().catch((err: unknown) => {
    // AbortError: a pause or another step came first (fine). NotAllowedError: the browser wants a gesture first.
    if (err instanceof DOMException && err.name === 'NotAllowedError') onBlocked();
  });
}

/** A step without playable audio: a timer drives the progress, resuming from where it was paused. */
function startTimer(step: TutorialStep, elapsed: { key: string; ms: number }, onProgress: (p: number) => void, onEnd: () => void): () => void {
  if (elapsed.key !== step.key) Object.assign(elapsed, { key: step.key, ms: 0 });
  const startedAt = performance.now() - elapsed.ms;
  const id = window.setInterval(() => {
    elapsed.ms = performance.now() - startedAt;
    const p = Math.min(1, elapsed.ms / step.durationMs);
    onProgress(p);
    if (p >= 1) {
      window.clearInterval(id);
      elapsed.ms = 0;
      onEnd();
    }
  }, 100);
  return () => window.clearInterval(id);
}

/** Only for a lesson without steps, which the catalog never opens. */
const NO_STEP: TutorialStep = { key: '', kind: null, title: '', narration: '', cues: [], image: null, audio: null, durationMs: 1000, startMs: null };

export function useLessonPlayer(lesson: TutorialLesson, { initialIndex, autoPlay = false, onStep, onFinished }: Options): LessonPlayer {
  const total = lesson.steps.length;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [index, setIndex] = useState(() => clampStep(initialIndex, total));
  const [playing, setPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [finished, setFinished] = useState(false);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const loaded = useRef<{ key: string; url: string } | null>(null);
  const elapsed = useRef({ key: '', ms: 0 });
  const step = lesson.steps[clampStep(index, total)] ?? NO_STEP;
  const latest = useRef({ step, index, onStep, onFinished });
  latest.current = { step, index, onStep, onFinished };

  const advance = useCallback(() => {
    const next = afterStepEnds(latest.current.index, total);
    setProgress(next.kind === 'step' ? 0 : 1);
    if (next.kind === 'step') return setIndex(next.index);
    setPlaying(false);
    setFinished(true);
    latest.current.onFinished();
  }, [total]);

  useEffect(() => latest.current.onStep(index), [index]);

  useEffect(() => {
    const audio = audioRef.current;
    const current = latest.current.step;
    if (!playing || !current) return void audio?.pause();
    if (current.audio && audio && failedKey !== current.key) {
      playAudio(audio, current, loaded.current, () => {
        setPlaying(false);
        setBlocked(true);
      });
      loaded.current = { key: current.key, url: current.audio };
      return () => audio.pause();
    }
    return startTimer(current, elapsed.current, setProgress, advance);
  }, [playing, index, failedKey, advance]);

  const goTo = useCallback(
    (target: number) => {
      setFinished(false);
      setProgress(0);
      elapsed.current = { key: '', ms: 0 };
      setIndex(clampStep(target, total));
    },
    [total],
  );

  const toggle = useCallback(() => {
    setBlocked(false);
    if (finished) {
      goTo(0);
      return setPlaying(true);
    }
    setPlaying((p) => !p);
  }, [finished, goTo]);

  const audioEvents = {
    onTimeUpdate: () => {
      const a = audioRef.current;
      if (a && a.duration > 0 && loaded.current?.key === latest.current.step?.key) setProgress(Math.min(1, a.currentTime / a.duration));
    },
    onEnded: () => {
      if (loaded.current?.key === latest.current.step?.key) advance();
    },
    onError: () => {
      if (latest.current.step?.audio) setFailedKey(latest.current.step.key);
    },
  };

  return { audioRef, index, step, playing, progress, finished, blocked, goTo, toggle, pause: () => setPlaying(false), audioEvents };
}
